/**
 * EdgeActions
 * -----------
 * Floating bottom-right action stack. Three slots, ordered LEFT → RIGHT
 * by "how present this is to the user right now":
 *
 *   1. Fullscreen toggle — ALWAYS visible. Most present because the
 *      user can hit it whenever. On mobile (Chrome / Safari address
 *      bar) and desktop (window chrome) this hands the user the full
 *      viewport for the 3D scene. Icon flips between "expand" and
 *      "compress" based on current fullscreen state, tracked via the
 *      standard `fullscreenchange` event (with webkit fallback for
 *      older Safari).
 *   2. Deselect "×" — visible only when there's a valid selection
 *      (the entity still exists in the dataset). Click clears the
 *      selection, which auto-closes the Detail drawer.
 *   3. Exit-plan door — visible whenever there's an active gameplan,
 *      regardless of which tab (Detail / List / Plan) the drawer is
 *      currently on. Click clears the active gameplan, which un-
 *      filters the 3D map back to the full graph. The user can
 *      always abandon the plan filter from any view this way; if
 *      this were gated to the Plan tab it'd be useless once they
 *      navigated away.
 *
 * Positioning rules (see `--bjjmap-drawer-width` / `--bjjmap-sheet-
 * height` CSS variables driven by ResizeHandle / PanelHandle):
 *   - Side mode (desktop / landscape): pinned to the right edge of
 *     the canvas pane = `right: var(--bjjmap-drawer-width) + 16px`.
 *     Stays in the canvas's bottom-right corner regardless of the
 *     side panel's width / drag.
 *   - Bottom-sheet mode (mobile portrait): pinned just above the
 *     sheet's top edge = `bottom: var(--bjjmap-sheet-height) + 12px`.
 *     Slides up with the user's drag-resize of the sheet, so the
 *     buttons remain reachable above the panel boundary.
 *
 * Each button has a generous 40×40 hit target so a thumb can tap it
 * reliably on a phone.
 */
import { useEffect, useState } from 'react';
import { useAppStore, isSelectionValid } from '../state/store';
import type { Counter, Move, Position } from '../types/domain';
import { useViewport } from './useViewport';

type Props = {
  positions: Position[];
  moves: Move[];
  counters: Counter[];
};

/**
 * Cross-browser fullscreen helpers. The standard API
 * (`document.fullscreenElement`, `requestFullscreen`,
 * `exitFullscreen`, `fullscreenchange`) covers Chrome, Edge,
 * Firefox, and modern Safari. The `webkit*` aliases catch older
 * iOS Safari and older Chrome-on-Android skins that still expose
 * only the prefixed surface.
 */
type FullscreenDoc = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
  webkitFullscreenEnabled?: boolean;
};
type FullscreenEl = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void>;
};

function isFullscreenActive(): boolean {
  if (typeof document === 'undefined') return false;
  const d = document as FullscreenDoc;
  return Boolean(d.fullscreenElement || d.webkitFullscreenElement);
}

/**
 * Runtime probe: does this browser actually support the Fullscreen
 * API on the document element?
 *
 * Notably returns `false` on every browser on iOS (Chrome, Firefox,
 * Edge, Safari) because Apple's App Store policy forces every iOS
 * browser to use WebKit, and WebKit's Fullscreen API support is
 * limited to `<video>` elements — `documentElement.requestFullscreen`
 * either rejects or is missing entirely. Hiding the control when
 * unsupported is a better UX than showing a button that silently
 * does nothing (iPhone users don't stare at a broken affordance
 * wondering if they're tapping it wrong).
 *
 * Android Chrome / all desktop browsers return `true` because Blink
 * and Gecko both implement the standard API.
 */
function isFullscreenSupported(): boolean {
  if (typeof document === 'undefined') return false;
  const d = document as FullscreenDoc;
  return Boolean(d.fullscreenEnabled || d.webkitFullscreenEnabled);
}

async function enterFullscreen(): Promise<void> {
  if (typeof document === 'undefined') return;
  const target = document.documentElement as FullscreenEl;
  try {
    if (target.requestFullscreen) {
      await target.requestFullscreen();
    } else if (target.webkitRequestFullscreen) {
      await target.webkitRequestFullscreen();
    }
  } catch (err) {
    // Some browsers (notably iOS Safari prior to 16.4) reject the
    // call entirely. We swallow because there's no graceful recovery
    // beyond the icon staying in "expand" state.
    console.warn('Fullscreen request rejected:', err);
  }
}

async function exitFullscreen(): Promise<void> {
  if (typeof document === 'undefined') return;
  const d = document as FullscreenDoc;
  try {
    if (d.exitFullscreen) {
      await d.exitFullscreen();
    } else if (d.webkitExitFullscreen) {
      await d.webkitExitFullscreen();
    }
  } catch (err) {
    console.warn('Fullscreen exit rejected:', err);
  }
}

export function EdgeActions({ positions, moves, counters }: Props) {
  const selection = useAppStore((s) => s.selection);
  const setSelection = useAppStore((s) => s.setSelection);
  const activeGameplanId = useAppStore((s) => s.activeGameplanId);
  const setActiveGameplan = useAppStore((s) => s.setActiveGameplan);
  // During the onboarding "controls" step, force all three buttons on and
  // flash them so the tour can point them out even with nothing selected.
  const coachControls = useAppStore((s) => s.coachHighlight === 'controls');
  const { isMobile, isPortrait } = useViewport();
  const bottomSheet = isMobile && isPortrait;

  // Feature-detect once on mount. document.fullscreenEnabled is stable
  // per-browser so a useState + noop-effect is enough — no need to
  // subscribe. Hidden on every iOS browser (WebKit limitation, see
  // isFullscreenSupported() for the full note); visible on Android
  // Chrome, desktop Chrome / Firefox / Safari / Edge.
  const [fullscreenSupported] = useState(isFullscreenSupported);

  // Local state mirrors the browser's fullscreen state so the icon
  // can flip without polling. Two listeners cover the prefixed and
  // standard event names; both are cheap no-ops on browsers that
  // only fire one of them. Skipped entirely on unsupported browsers.
  const [fullscreen, setFullscreen] = useState(isFullscreenActive);
  useEffect(() => {
    if (!fullscreenSupported) return;
    const sync = () => setFullscreen(isFullscreenActive());
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, [fullscreenSupported]);

  // Only offer "deselect" when the selection actually has somewhere
  // to deselect FROM — a stale id pointing at a removed entity
  // would create a button that doesn't visibly change anything.
  const hasValidSelection = isSelectionValid(
    selection,
    positions,
    moves,
    counters,
  );
  // Show the exit-plan door whenever there's an active gameplan —
  // even if the user has navigated AWAY from the Plan tab. The 3D
  // map remains filtered to the active plan in that state, so
  // exiting needs to be available from Detail / List too.
  const showExitPlan = activeGameplanId !== null;

  // Fullscreen toggle is always rendered, so the stack always shows.
  // (Previously this component bailed out entirely when no selection /
  // plan was active — now there's always at least one button.)

  // Position primitives — CSS variable arithmetic keeps the stack
  // glued to the canvas/panel boundary on both orientations and
  // tracks any live drag of either ResizeHandle.
  //
  // Tightened gap (8px in side mode, 6px in bottom-sheet) so the
  // stack reads as visually anchored to the panel boundary. With
  // the previous 16/12px gap the buttons looked "floating in the
  // middle" in landscape mobile, where the canvas pane occupies
  // only half the viewport and the stack landed at viewport-center.
  // Hugging the boundary makes the buttons feel like they belong
  // to the canvas/panel seam.
  const positionStyle = bottomSheet
    ? {
        position: 'fixed' as const,
        right: 14,
        bottom: 'calc(var(--bjjmap-sheet-height, 0px) + 6px)',
      }
    : {
        position: 'fixed' as const,
        bottom: 18,
        right: 'calc(var(--bjjmap-drawer-width, 0px) + 8px)',
      };

  return (
    <div
      data-tour="controls"
      style={{
        ...positionStyle,
        zIndex: 35,
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        // Smooth slide with the panel boundary on resize.
        transition:
          'right 220ms cubic-bezier(0.2, 0.8, 0.2, 1), bottom 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
      }}
    >
      {/* Order: Fullscreen (when supported) → Deselect × (when
          valid) → Exit-plan door (when an active plan exists).
          Most-present on the left so the user's eye lands on the
          leading control first; the conditional slots cluster to
          the right where they read as "extra" affordances.

          Fullscreen is gated on runtime feature-detection because
          every iOS browser (Chrome, Firefox, Edge, Safari) runs on
          WebKit and doesn't support Fullscreen API on document
          elements — showing a button that silently does nothing
          confused iPhone users. Android / desktop still see it. */}
      {(fullscreenSupported || coachControls) && (
        <ActionButton
          flash={coachControls}
          onClick={() => {
            if (fullscreen) {
              void exitFullscreen();
            } else {
              void enterFullscreen();
            }
          }}
          title={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          aria-label={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {fullscreen ? (
            // "Compress" glyph — four corner brackets pointing INWARD.
            // Reads as "shrink back to normal".
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              aria-hidden
            >
              <path d="M6 2v4H2" />
              <path d="M10 2v4h4" />
              <path d="M6 14v-4H2" />
              <path d="M10 14v-4h4" />
            </svg>
          ) : (
            // "Expand" glyph — four corner brackets pointing OUTWARD.
            // Reads as "grow to fill the viewport".
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              aria-hidden
            >
              <path d="M2 6V2h4" />
              <path d="M14 6V2h-4" />
              <path d="M2 10v4h4" />
              <path d="M14 10v4h-4" />
            </svg>
          )}
        </ActionButton>
      )}
      {(hasValidSelection || coachControls) && (
        <ActionButton
          flash={coachControls}
          onClick={() => setSelection({ kind: 'none' })}
          title="Deselect"
          aria-label="Deselect node"
        >
          {/* × glyph centered in a 40px hit target. */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            fill="none"
            aria-hidden
          >
            <line x1="3" y1="3" x2="13" y2="13" />
            <line x1="13" y1="3" x2="3" y2="13" />
          </svg>
        </ActionButton>
      )}
      {(showExitPlan || coachControls) && (
        <ActionButton
          flash={coachControls}
          onClick={() => {
            // Clear the active plan. Drops the map's gameplan
            // filter and lets the user navigate the full graph
            // again. Stays on whatever tab (Detail / List / Plan)
            // they were on so context isn't yanked.
            setActiveGameplan(null);
          }}
          title="Exit gameplan"
          aria-label="Exit gameplan filter"
        >
          {/* Exit-door glyph: rectangle + arrow heading out the
              right edge. */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            aria-hidden
          >
            <path d="M9 2H3v12h6" />
            <path d="M7 8h7" />
            <path d="M11 5l3 3-3 3" />
          </svg>
        </ActionButton>
      )}
    </div>
  );
}

/** Shared 40×40 button used by both action slots. Neutral surface that
 *  matches PanelHandle's visual weight so the bottom edge of the
 *  canvas reads as a single floating control band. */
function ActionButton({
  children,
  onClick,
  title,
  flash,
  ...aria
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  flash?: boolean;
  'aria-label': string;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={aria['aria-label']}
      style={{
        width: 40,
        height: 40,
        padding: 0,
        borderRadius: 10,
        background: 'rgba(20,22,30,0.92)',
        border: '1px solid rgba(255,255,255,0.12)',
        backdropFilter: 'blur(10px)',
        color: '#e6e8ef',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 120ms ease, color 120ms ease',
        animation: flash
          ? 'bjjmap-ctrl-flash 2.2s ease-in-out infinite'
          : undefined,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          'rgba(40,44,58,0.96)';
        (e.currentTarget as HTMLButtonElement).style.color = '#ffffff';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          'rgba(20,22,30,0.92)';
        (e.currentTarget as HTMLButtonElement).style.color = '#e6e8ef';
      }}
    >
      {children}
    </button>
  );
}
